import React, { useState, useEffect } from 'react'
import produce from 'immer'
import { useDebounce } from 'use-lodash-debounce'
import { Wrapper, TableWrapper, Table, THead, TBody, TFoot, TR, TH, TD, Input, Select, Checkbox, EditButton, EditIcon, ButtonsWrapper, AddButton, Tooltip } from './styled'

const widths = {
  default: 220,
  number: 110,
  select: 180,
  checkbox: 20,
  editButton: 40
}

const InputTable = props => {
  const { builder = false, extra = {}, id, save = () => { }, value, validationError = {} } = props
  const { columns = [], defaultRows = [], dynamicRows = false, addRowText = '' } = extra

  const [mounted, setMounted] = useState(false)
  const [rows, setRows] = useState(value || defaultRows)
  const debouncedRows = useDebounce(rows, 500, { trailing: true })

  useEffect(() => {
    if (mounted) {
      if (builder) {
        save({ _id: id, key: 'defaultRows', value: debouncedRows })
        return
      }

      save({ key: id, value: debouncedRows })
    }
  }, [debouncedRows])

  useEffect(() => {
    if (builder && mounted && rows.length && columns.length !== rows[0].columns.length) {
      setRows(produce(draft => {
        if (columns.length > rows[0].columns.length) {
          draft.map(({ columns: cols }) => cols.push({ columnId: [...columns].pop().id, value: '' }))
        }
        else {
          const columnIds = columns.map(({ id }) => id)
          const colIdx = rows[0].columns.findIndex(({ columnId }) => !columnIds.includes(columnId))
          draft.map(({ columns: cols }) => cols.splice(colIdx, 1))
        }
      }))
    }
  }, [columns])

  useEffect(() => {
    setMounted(true)
  }, [])

  const renderInput = (type, value, rowIdx, colIdx) => {
    switch (type) {
      case 'checkbox':
        return (
          <Checkbox checked={value} onClick={() => onChange(!value, rowIdx, colIdx)}>
            <input type="checkbox" checked={value} onChange={() => onChange(!value, rowIdx, colIdx)} />
          </Checkbox>
        )
      case 'select':
        return (
          <Select value={value} onChange={e => onChange(e.target.value, rowIdx, colIdx)}>
            {
              columns[colIdx].options.map((value, i) => <option key={i} value={value}>{value}</option>)
            }
          </Select>
        )
      default:
        return <Input type={type} value={value} onChange={e => onChange(e.target.value, rowIdx, colIdx)} />
    }
  }

  const onChange = (value, rowIdx, colIdx) => {
    setRows(produce(draft => {
      draft[rowIdx].columns[colIdx].value = value
    }))
  }

  const addRow = () => {
    const newRow = {
      locked: false,
      columns: Array(columns.length).fill('').map((value, i) => ({ columnId: columns[i].id, value }))
    }

    setRows(produce(draft => {
      draft.push(newRow)
    }))
  }

  const deleteRow = rowIdx => {
    setRows(produce(draft => {
      draft.splice(rowIdx, 1)
    }))
  }

  const sumColumn = colIdx => {
    return rows.reduce((acc, cur) => acc + Number(cur.columns[colIdx].value), 0)
  }

  const toggleLockRow = rowIdx => {
    setRows(produce(draft => {
      draft[rowIdx].locked = !draft[rowIdx].locked
    }))
  }

  if (!columns.length) {
    return null
  }

  return (
    <Wrapper className="InputTable">
      <TableWrapper>
        <Table>
          <THead>
            <TR>
              {
                columns.map(({ label, type }, i) => (
                  <TH key={i} minWidth={widths[type] || widths.default} checkbox={type === 'checkbox'}>{label}</TH>
                ))
              }
              {
                builder && <TH width={widths.editButton} />
              }
              <TH width={widths.editButton}></TH>
            </TR>
          </THead>
          <TBody>
            {
              rows.map((row, rowIdx) => (
                <TR key={rowIdx}>
                  {
                    columns.map((column, colIdx) => {
                      if (!row.columns[colIdx]) {
                        return null
                      }

                      const { critical = {} } = validationError

                      const error = critical[`${rowIdx}:${colIdx}`]

                      return (
                        <TD key={colIdx} checkbox={column.type === 'checkbox'} error={error}>
                          {
                            renderInput(column.type, row.columns[colIdx].value, rowIdx, colIdx)
                          }
                          {
                            error && <Tooltip>{error}</Tooltip>
                          }
                        </TD>
                      )
                    })
                  }
                  {
                    builder && (
                      <TD>
                        <EditButton onClick={() => toggleLockRow(rowIdx)}>
                          <EditIcon className={row.locked ? 'far fa-lock' : 'far fa-lock-open'} />
                        </EditButton>
                      </TD>
                    )
                  }
                  <TD>
                    <EditButton onClick={() => deleteRow(rowIdx)} disabled={!builder && row.locked}>
                      <EditIcon className="far fa-times" />
                    </EditButton>
                  </TD>
                </TR>
              ))
            }
          </TBody>
          {
            rows.length > 0 && columns.some(({ sum }) => sum) && (
              <TFoot>
                <TR>
                  {
                    columns.map(({ sum = false }, i) => (
                      <TD key={i}>{sum && sumColumn(i)}</TD>
                    ))
                  }
                  <TD />
                  {
                    builder && <TD />
                  }
                </TR>
              </TFoot>
            )
          }
        </Table>
      </TableWrapper>
      {
        (builder || dynamicRows) && (
          <ButtonsWrapper>
            <AddButton onClick={addRow}>{addRowText ? addRowText : 'Add row'}</AddButton>
          </ButtonsWrapper>
        )
      }
    </Wrapper>
  )
}

export default InputTable